/**
 * Market data hooks using TanStack Query for caching, deduplication, and background refresh.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { marketDataService } from '@/services';
import { useMarketStore } from '@/store';
import { useEffect } from 'react';
import type { CandleQuery, IndexHistoryQuery } from '@/types';

export const MARKET_QUERY_KEYS = {
  quote: (symbol: string) => ['market', 'quote', symbol] as const,
  // Copies before sorting: `symbols.sort()` sorts in place, which silently reordered the
  // caller's own array — the Market overview's volume ranking was alphabetised by the
  // act of building its cache key, and only a row-order assertion catches that.
  quotes: (symbols: string[]) => ['market', 'quotes', ...[...symbols].sort()] as const,
  detail: (symbol: string) => ['market', 'detail', symbol] as const,
  historical: (symbol: string, from: string, to: string) => ['market', 'historical', symbol, from, to] as const,
  kse100: () => ['market', 'kse100'] as const,
  index: (symbol: string) => ['market', 'index', symbol.toUpperCase()] as const,
  status: () => ['market', 'status'] as const,
  search: (query: string) => ['market', 'search', query] as const,
  sectors: () => ['market', 'sectors'] as const,
  dividends: (from: string) => ['market', 'dividends', from] as const,
} as const;

/**
 * The exchange's calendar day, not the viewer's. The dividend ledger is filtered
 * on the announcement date, and a viewer in another timezone asking at 23:30 UTC
 * would otherwise ask for "yesterday" relative to Karachi.
 */
export function todayInKarachi(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Karachi' }).format(new Date());
}

/**
 * Announced dividends dated today or later. One request covers the window — the
 * upcoming ledger is a handful of rows — and it is refetched slowly: announcements
 * are published a few times a day, not tick by tick.
 */
export function useDividendAnnouncements(from: string = todayInKarachi()) {
  return useQuery({
    queryKey: MARKET_QUERY_KEYS.dividends(from),
    queryFn: () => marketDataService.getDividendLedger({ from }),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Fetch and cache a single stock quote */
export function useStockQuote(symbol: string | undefined) {
  const setQuote = useMarketStore((s) => s.setQuote);
  const query = useQuery({
    queryKey: symbol ? MARKET_QUERY_KEYS.quote(symbol) : ['disabled'],
    queryFn: () => marketDataService.getQuote(symbol!),
    enabled: !!symbol,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (query.data) setQuote(query.data);
  }, [query.data, setQuote]);

  return query;
}

/** Fetch multiple quotes in a single call */
export function useStockQuotes(symbols: string[]) {
  const setQuotes = useMarketStore((s) => s.setQuotes);
  const query = useQuery({
    queryKey: MARKET_QUERY_KEYS.quotes(symbols),
    queryFn: () => marketDataService.getQuotes(symbols),
    enabled: symbols.length > 0,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (query.data) setQuotes(query.data);
  }, [query.data, setQuotes]);

  return query;
}

/** Fetch full stock details (richer data, longer cache) */
export function useStockDetail(symbol: string | undefined) {
  return useQuery({
    queryKey: symbol ? MARKET_QUERY_KEYS.detail(symbol) : ['disabled'],
    queryFn: () => marketDataService.getStockDetail(symbol!),
    enabled: !!symbol,
    staleTime: 300_000,
  });
}

/** Fetch historical OHLCV data */
export function useHistoricalData(symbol: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: symbol ? MARKET_QUERY_KEYS.historical(symbol, from, to) : ['disabled'],
    queryFn: () => marketDataService.getHistoricalData(symbol!, from, to),
    enabled: !!symbol && !!from && !!to,
    staleTime: 3_600_000, // 1 hour
  });
}

/** Fetch KSE100 index. Resolves to null when the provider has no index to offer. */
export function useKSE100(opts: IndexHistoryQuery = {}) {
  const setKSE100 = useMarketStore((s) => s.setKSE100);
  const query = useQuery({
    // The window is part of the key: a 1M comparison must not reuse the 1Y series.
    queryKey: [...MARKET_QUERY_KEYS.kse100(), opts.from ?? '', opts.to ?? ''] as const,
    queryFn: () => marketDataService.getKSE100(opts),
    staleTime: 60_000,
    // No index available (provider returned null) → stop polling for it. The provider
    // short-circuits those calls anyway; this just stops re-running the query.
    refetchInterval: (q) => (q.state.data ? 60_000 : false),
  });

  useEffect(() => {
    if (query.data) setKSE100(query.data);
  }, [query.data, setKSE100]);

  return query;
}

/**
 * Fetch any PSX index by code (KSE100, KSE30, ALLSHR, KMI30, …).
 *
 * Resolves to `null` when the feed does not track that index yet — callers render
 * "not tracked", never a substituted number.
 */
export function useIndex(symbol: string | undefined, opts: IndexHistoryQuery = {}) {
  return useQuery({
    queryKey: symbol ? [...MARKET_QUERY_KEYS.index(symbol), opts.from ?? '', opts.to ?? ''] : ['disabled'],
    queryFn: () => marketDataService.getIndex(symbol!, opts),
    enabled: !!symbol,
    staleTime: 60_000,
    // Null means "the feed doesn't track this one" — polling wouldn't change that.
    refetchInterval: (q) => (q.state.data ? 60_000 : false),
  });
}

/**
 * Daily candles for a symbol — the feed's own chart series, dated by exchange session.
 *
 * This is what the comparison plots: candle rows carry the session day, so the two
 * lines can't drift apart the way UTC-stamped `/history` rows can.
 */
export function useCandles(symbol: string | undefined, opts: CandleQuery = {}) {
  const { range, from, to } = opts;
  return useQuery({
    queryKey: ['market', 'candles', symbol ?? '', range ?? '', from ?? '', to ?? ''] as const,
    queryFn: () => marketDataService.getCandles(symbol!, { range, from, to }),
    enabled: !!symbol,
    staleTime: 30 * 60_000,
  });
}

/** Fetch market open/close status */
export function useMarketStatus() {
  return useQuery({
    queryKey: MARKET_QUERY_KEYS.status(),
    queryFn: () => marketDataService.getMarketStatus(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** Search companies by name or symbol */
export function useCompanySearch(query: string) {
  return useQuery({
    queryKey: MARKET_QUERY_KEYS.search(query),
    queryFn: () => marketDataService.searchCompanies(query),
    enabled: query.length >= 1,
    staleTime: 300_000,
    // Hold the previous results while the next query is in flight, so the list never blanks
    // out mid-typing.
    placeholderData: keepPreviousData,
  });
}

/**
 * The market-wide sector board, from the PSX scraper: every PSX sector, with the
 * feed's own average move for the session. The dashboard narrows it to the sectors
 * the signed-in account actually holds before plotting. It only changes once per
 * session, so it is cached for five minutes rather than polled with the quotes.
 */
export function useSectorPerformance() {
  return useQuery({
    queryKey: MARKET_QUERY_KEYS.sectors(),
    queryFn: () => marketDataService.getSectorPerformance(),
    staleTime: 300_000,
    refetchInterval: 300_000,
  });
}
